import { emitCustomEvent } from 'react-custom-events';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function handleResponse<T>(response: Response): Promise<T | undefined> {
    if (!response.ok) {
        if (response.status === 401) {
            emitCustomEvent('lediumUnauthorized', undefined);
            return undefined;
        }
        let error = 'Network error!';
        if (response.status >= 500) {
            error = 'Server error!';
        } else if (response.status === 400) {
            error = 'Validation error!';
        }
        emitCustomEvent('lediumApiError', error);
        return undefined;
    }
    try {
        const result = (await response.json()) as T;
        return result;
    } catch {
        emitCustomEvent('lediumApiError', 'Network error!');
        return undefined;
    }
}

function fetchCall<T>(method: HttpMethod) {
    return async (path: string, data: unknown = {}, signal?: AbortSignal): Promise<T | undefined> => {
        const url = '/api/' + path;
        const needsBody = ['PATCH', 'PUT', 'POST'].includes(method);
        const options: RequestInit = { method, signal };
        if (needsBody) {
            options.headers = { 'Content-Type': 'application/json' };
            options.body = JSON.stringify(data);
        }

        let response: Response;
        try {
            response = await fetch(url, options);
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') {
                return undefined;
            }
            emitCustomEvent('lediumApiError', 'Network error!');
            return undefined;
        }

        return handleResponse<T>(response);
    };
}

async function fetchFormCall<T>(path: string, formData: FormData): Promise<T | undefined> {
    let response: Response;
    try {
        response = await fetch('/api/' + path, { method: 'POST', body: formData });
    } catch {
        emitCustomEvent('lediumApiError', 'Network error!');
        return undefined;
    }
    return handleResponse<T>(response);
}

const lediumApi = {
    get: <T>(path: string, signal?: AbortSignal) => fetchCall<T>('GET')(path, undefined, signal),
    post: <T>(path: string, data: unknown) => fetchCall<T>('POST')(path, data),
    delete: <T>(path: string) => fetchCall<T>('DELETE')(path),
    put: <T>(path: string, data: unknown) => fetchCall<T>('PUT')(path, data),
    patch: <T>(path: string, data: unknown) => fetchCall<T>('PATCH')(path, data),
    postForm: <T>(path: string, formData: FormData) => fetchFormCall<T>(path, formData)
};

export default lediumApi;
